'use client'

import { useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'

import { commonSearchParamsSchema } from '../../lib/common-search-params'
import { getHeaderBranding } from '../../lib/get-header-branding'
import { Venue } from '../App.types'
import { getGraphqlForClient } from '../lib/get-graphql-for-client'
import { ThemeProvider } from '../lib/ThemeProvider'

import { ForbiddenContent, ForbiddenFooter } from './components/Forbidden'

interface WidgetBrandSettings {
  displayName?: string
  primaryColor: string
  logo?: {
    xSmall?: string
  }
}

interface CompanyWithBrandSettings {
  id: string
  name: string
  widgetBrandSettings: WidgetBrandSettings
  venues: Array<Pick<Venue, 'id' | 'name' | 'logo'>>
}

export default function Forbidden() {
  const [company, setCompany] = useState<CompanyWithBrandSettings | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const searchParams = useSearchParams()

  useEffect(() => {
    const fetchCompanyData = async () => {
      const searchParamsObj = Object.fromEntries(searchParams.entries())
      const urlSchemaValidation = commonSearchParamsSchema.safeParse(searchParamsObj)
      const companyId = urlSchemaValidation.success ? urlSchemaValidation.data.companyId : undefined

      if (companyId) {
        const graphql = getGraphqlForClient()
        const { company: companyData } = await graphql.ForbiddenCompany({
          companyId,
        })
        setCompany(companyData)
      }
      setIsLoading(false)
    }

    void fetchCompanyData()
  }, [searchParams])

  if (isLoading || !company) {
    return null
  }

  const primaryColor = company.widgetBrandSettings.primaryColor

  const { headerName: displayName, headerLogoSrc: logoUrl } = getHeaderBranding({
    venue: company.venues?.[0],
    company,
  })

  return (
    <ThemeProvider companyPrimaryColor={primaryColor}>
      <div className="flex min-h-screen flex-col">
        <ForbiddenContent logoUrl={logoUrl} displayName={displayName} companyId={company?.id} />
        <ForbiddenFooter />
      </div>
    </ThemeProvider>
  )
}
